// src/components/VaccineSaleList.js
import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Spinner,
  Text,
  FormControl,
  FormLabel,
  Input,
  Select,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines, faSearch } from "@fortawesome/free-solid-svg-icons";
import DataTable from "./AppDataTable";
import { tableCustomStyles } from "../themes/TableStyles";
import apiService from "../services/ApiService";
import { useAuth } from "../context/AuthContext";
import * as IconButton from "../themes/IconButtonSet";
import { Search, Clear } from "../themes/IconButtonSet";
import { StandardHeader } from "../themes/StandardHeader";
import CustomDatePicker, { convertToThDate } from "./CustomDatePicker";

const getDefaultFilters = () => {
  const today = new Date();
  const startDate = new Date();
  startDate.setDate(today.getDate() - 15);
  return {
    StartDate: startDate,
    EndDate: today,
    PickupPoint: "",
    FarmerName: "",
    TransactionID: "",
  };
};

const VaccineSaleList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId } = useAuth();
  const today = new Date();

  const [sales, setSales] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filterData, setFilterData] = useState(getDefaultFilters);

  const minDateForEndDate = filterData.StartDate
    ? new Date(filterData.StartDate)
    : undefined;

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  const columns = [
    {
      name: "เลขที่เอกสาร",
      selector: (row) => row.TransactionID,
      sortable: true,
      width: "250px",
    },
    {
      name: "วันที่จำหน่าย",
      selector: (row) => convertToThDate(row.RequestDate),
      sortable: true,
      width: "180px",
    },
    { name: "ชื่อเกษตรกร", selector: (row) => row.FarmerName, sortable: true },
    {
      name: "จุดจำหน่าย",
      selector: (row) => {
        const warehouse = warehouses.find(
          (w) => w.WarehouseID === row.PickupPoint
        );
        return warehouse ? warehouse.WarehouseName : row.PickupPoint || "-";
      },
      sortable: true,
    },
    {
      name: "การดำเนินการ",
      cell: (row) => (
        <IconButton.View
          icon={faFileLines}
          onClick={() => navigate(`/vaccinesale/${row.TransactionID}`)}
          label="ดูรายละเอียด"
          width="180px"
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "120px",
    },
  ];

  const fetchWarehouses = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinePickupPointList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setWarehouses(response.VaccineWarehousesList || []);
      }
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    }
  };

  const fetchSales = async (filters = filterData) => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineSaleOrdersList",
        data: {
          StartDate: filters.StartDate || "",
          EndDate: filters.EndDate || "",
          PickupPoint: filters.PickupPoint || "",
          FarmerName: filters.FarmerName?.trim() || "",
          TransactionID: filters.TransactionID?.trim() || "",
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setSales(response.VaccineSaleOrdersList || []);
      } else {
        setSales([]);
      }
    } catch (error) {
      console.error("Error fetching sales:", error);
      setSales([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchWarehouses();
      fetchSales(getDefaultFilters());
    }
  }, [sessionId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilterData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearch = () => {
    fetchSales(filterData);
  };

  const handleClearSearch = () => {
    const resetFilters = getDefaultFilters();
    setFilterData(resetFilters);
    fetchSales(resetFilters);
  };

  const handleCreateNew = () => {
    navigate("/vaccinesale/new");
  };

  if (!sessionId) {
    return null;
  }

  return (
    <Box
      p={{ base: 4, md: 10, lg: 35 }}
      m={{ base: 2, md: 10, lg: 25 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <VStack spacing={6} align="start">
        <StandardHeader
          title="รายการจำหน่ายวัคซีน"
          bgColor="iBlue.600"
          icon={faFileLines}
        />

        <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} w="100%">
          <StandardHeader
            title="ค้นหาข้อมูลการจำหน่ายวัคซีน"
            bgColor="iBlue.600"
            icon={faSearch}
          />
          <VStack spacing={6} p={5}>
            <HStack spacing={6} w="100%" flexWrap="wrap">
              <FormControl minW="200px" flex="1">
                <FormLabel fontSize="xl" color="gray">
                  วันที่เริ่มต้น
                </FormLabel>
                <CustomDatePicker
                  name="StartDate"
                  value={filterData.StartDate}
                  onChange={handleInputChange}
                  colorScheme="iBlue"
                  maxDate={today}
                />
              </FormControl>
              <FormControl minW="200px" flex="1">
                <FormLabel fontSize="xl" color="gray">
                  วันที่สิ้นสุด
                </FormLabel>
                <CustomDatePicker
                  name="EndDate"
                  value={filterData.EndDate}
                  onChange={handleInputChange}
                  colorScheme="iBlue"
                  minDate={minDateForEndDate}
                  maxDate={today}
                />
              </FormControl>
              <FormControl minW="200px" flex="1">
                <FormLabel fontSize="xl" color="gray">
                  จุดจำหน่าย
                </FormLabel>
                <Select
                  name="PickupPoint"
                  value={filterData.PickupPoint}
                  onChange={handleInputChange}
                  placeholder="เลือกจุดจำหน่าย (ทั้งหมด)"
                  colorScheme="iBlue"
                >
                  {warehouses.map((warehouse) => (
                    <option
                      key={warehouse.WarehouseID}
                      value={warehouse.WarehouseID}
                    >
                      {warehouse.WarehouseName}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl minW="200px" flex="1">
                <FormLabel fontSize="xl" color="gray">
                  เลขที่เอกสาร
                </FormLabel>
                <Input
                  name="TransactionID"
                  value={filterData.TransactionID}
                  onChange={handleInputChange}
                  placeholder="ค้นหาเลขที่เอกสาร"
                  colorScheme="iBlue"
                />
              </FormControl>
              <FormControl minW="200px" flex="1">
                <FormLabel fontSize="xl" color="gray">
                  ชื่อเกษตรกร
                </FormLabel>
                <Input
                  name="FarmerName"
                  value={filterData.FarmerName}
                  onChange={handleInputChange}
                  placeholder="ค้นหาชื่อเกษตรกร"
                  colorScheme="iBlue"
                />
              </FormControl>
            </HStack>
            <Box w="100%" textAlign="right">
              <HStack spacing={5} justifyContent="flex-end">
                <Clear onClick={handleClearSearch} />
                <Search onClick={handleSearch} isLoading={isLoading} />
              </HStack>
            </Box>
          </VStack>
        </Box>

        <HStack w="100%" justify="flex-end">
          <IconButton.Add onClick={handleCreateNew} label="เพิ่มรายการใหม่" />
        </HStack>

        {isLoading ? (
          <Spinner size="md" />
        ) : sales.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ไม่พบข้อมูลใบจำหน่ายวัคซีนตามเงื่อนไขที่ค้นหา
          </Text>
        ) : (
          <Box w="100%" overflowX="auto">
            <DataTable
              columns={columns}
              data={sales}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
            />
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default VaccineSaleList;
